// src/components/anagrafiche/clienti/clientiSlice.ts

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
    ErrorStatusTypes,
    ErrorsStack,
    parseErrorMessage,
} from "components/common/errorsDeclarations";
import * as clientiApi from "api/anagrafiche/clienti/clientiApi";
import { toast } from "react-toastify";

// Enum per tipo cliente

// Interfacce per Cliente
export interface ClienteLight {
    id?: number;
    codice_cliente_legacy?: number;
    nome_cliente?: string;
}
// Interfacce per Cliente
export interface Cliente extends ClienteLight {
    errorsStack?: ErrorsStack;
}

export interface Clienti {
    count: number;
    page: number;
    num_pages: number;
    next?: string;
    previous?: string;
    results: Cliente[];
    errorsStack: ErrorsStack;
}

export interface FiltriClienti {
    search?: string;
    page?: number;
    numero_record?: number;
}

// Stato del slice
export interface ClientiState {
    clienti: Clienti;
    clienteCorrenteId?: number;
    filtri: FiltriClienti;
}

const initialState: ClientiState = {
    clienti: {
        count: 0,
        page: 0,
        num_pages: 0,
        next: undefined,
        previous: undefined,
        results: [],
        errorsStack: { status: ErrorStatusTypes.OK },
    },
    clienteCorrenteId: undefined,
    filtri: {},
};

// AsyncThunk actions
export const fetchClienti = createAsyncThunk(
    "clienti/fetchClienti",
    async (parametri: FiltriClienti = {}) => {
        return await clientiApi.fetchClienti(parametri);
    }
);

export const getCliente = createAsyncThunk(
    "clienti/getCliente",
    async (parametri: { cliente_id: number }) => {
        return await clientiApi.getCliente(parametri.cliente_id);
    }
);

export const saveCliente = createAsyncThunk(
    "clienti/saveCliente",
    async (parametri: { clienteToSave: Cliente }) => {
        return await clientiApi.saveCliente(parametri.clienteToSave);
    }
);

export const deleteCliente = createAsyncThunk(
    "clienti/deleteCliente",
    async (parametri: { clienteToDelete: Cliente }) => {
        return await clientiApi.deleteCliente(parametri.clienteToDelete);
    }
);

// Slice
export const clientiSlice = createSlice({
    name: "clienti",
    initialState,
    reducers: {
        setClienteCorrenteId: (state, action: PayloadAction<number | undefined>) => {
            state.clienteCorrenteId = action.payload;
        },
        setFiltri: (state, action: PayloadAction<FiltriClienti>) => {
            state.filtri = { ...state.filtri, ...action.payload };
        },
        resetFiltri: (state) => {
            state.filtri = {};
        },
        resetClienteCorrente: (state) => {
            state.clienteCorrenteId = undefined;
        },
    },
    extraReducers: (builder) => {
        // fetch Clienti
        builder.addCase(fetchClienti.pending, (state) => {
            state.clienti.errorsStack = { status: ErrorStatusTypes.PENDING };
        });
        builder.addCase(fetchClienti.fulfilled, (state, action) => {
            state.clienti = action.payload;
            state.clienti.errorsStack = { status: ErrorStatusTypes.OK };
        });
        builder.addCase(fetchClienti.rejected, (state, action) => {
            state.clienti.errorsStack = parseErrorMessage(action.error);
        });

        // get Cliente
        builder.addCase(getCliente.pending, (state) => {
            state.clienti.errorsStack = { status: ErrorStatusTypes.PENDING };
        });
        builder.addCase(getCliente.fulfilled, (state, action) => {
            // Rimuovi il cliente esistente se presente
            state.clienti.results = state.clienti.results.filter(
                (cliente) => cliente.id !== action.payload.id
            );
            // Aggiungi il cliente aggiornato
            state.clienti.results.push(action.payload);
            state.clienteCorrenteId = action.payload.id;
            state.clienti.errorsStack = { status: ErrorStatusTypes.OK };
        });
        builder.addCase(getCliente.rejected, (state, action) => {
            state.clienti.errorsStack = parseErrorMessage(action.error);
        });

        // save Cliente
        builder.addCase(saveCliente.pending, (state) => {
            state.clienti.errorsStack = { status: ErrorStatusTypes.PENDING };
        });
        builder.addCase(saveCliente.fulfilled, (state, action) => {
            // Aggiorna la lista se il cliente è già presente
            const index = state.clienti.results.findIndex(
                (cliente) => cliente.id === action.payload.id
            );
            if (index !== -1) {
                state.clienti.results[index] = {
                    ...action.payload,
                    errorsStack: { status: ErrorStatusTypes.SUCCESS },
                };
            } else {
                // Aggiungi il nuovo cliente
                state.clienti.results.unshift({
                    ...action.payload,
                    errorsStack: { status: ErrorStatusTypes.SUCCESS },
                });
                state.clienti.count++;
            }

            state.clienteCorrenteId = action.payload.id;
            state.clienti.errorsStack = { status: ErrorStatusTypes.SUCCESS };

            toast.success("Cliente salvato con successo");
        });
        builder.addCase(saveCliente.rejected, (state, action) => {
            // Aggiorna l'errore sul cliente specifico se esiste
            state.clienti.results = state.clienti.results.map((cliente) => {
                if (cliente.id === action.meta.arg.clienteToSave.id) {
                    return {
                        ...cliente,
                        errorsStack: parseErrorMessage(action.error),
                    };
                }
                return cliente;
            });
            state.clienti.errorsStack = parseErrorMessage(action.error);
        });

        // delete Cliente
        builder.addCase(deleteCliente.pending, (state) => {
            state.clienti.errorsStack = { status: ErrorStatusTypes.PENDING };
        });
        builder.addCase(deleteCliente.fulfilled, (state, action) => {
            state.clienti.results = state.clienti.results.filter(
                (cliente) => cliente.id !== action.meta.arg.clienteToDelete.id
            );
            state.clienti.count--;
            state.clienti.errorsStack = { status: ErrorStatusTypes.OK };

            toast.success("Cliente eliminato con successo");
        });
        builder.addCase(deleteCliente.rejected, (state, action) => {
            state.clienti.errorsStack = parseErrorMessage(action.error);
            toast.error("Errore nell'eliminazione del cliente");
        });
    },
});

// Action creators
export const {
    setClienteCorrenteId,
    setFiltri,
    resetFiltri,
    resetClienteCorrente,
} = clientiSlice.actions;

export const clientiSliceReducer = clientiSlice.reducer;